import {
  selectOrganizationId,
  useGetIntegrationsConfigQuery,
} from '@carboncopyco/data';
import { InfoOutlineIcon } from '@carboncopyco/icons';
import type { Opportunity, OpportunityFilter } from '@carboncopyco/types';
import {
  ColorHex,
  DropdownFilter,
  Paper,
  Separator,
  TextBoxEditorTitle,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { integrationVendorNameToTitleMap } from '../consts';
import { FreeformTextOption } from './FreeformTextOption';
import { OpportunityList } from './OpportunityList';

type OpportunityOptionsProps = {
  query: string;
  freeformText: string;
  opportunities: Opportunity[];
  isLoading: boolean;
  isIntegrationConnected: boolean;
  opportunityFilter: OpportunityFilter;
  setOpportunityFilter: (filter: OpportunityFilter) => void;
};

export const OpportunityOptions = ({
  query,
  freeformText,
  opportunities,
  isLoading,
  isIntegrationConnected,
  opportunityFilter,
  setOpportunityFilter,
}: OpportunityOptionsProps) => {
  const organizationIdFromStore = useSelector(selectOrganizationId);
  const { organizationId: organizationIdFromParams } = useParams();
  const organizationId = organizationIdFromParams || organizationIdFromStore;
  const [debouncedQuery] = useDebounce(query, 300);
  const { data: config } = useGetIntegrationsConfigQuery({
    organizationId,
  });
  const integrationType = config?.crm;
  const name = integrationVendorNameToTitleMap[integrationType];
  const filteredOpportunities =
    opportunityFilter === 'opportunitiesExceptCurrentUser' ||
    debouncedQuery === ''
      ? opportunities
      : opportunities.filter((opportunity) =>
          opportunity.name.toLowerCase().includes(debouncedQuery.toLowerCase())
        );

  const infoRef = useRef<HTMLDivElement>(null);
  const parentRef = React.useRef<HTMLDivElement>(null);

  return (
    <Paper
      radius
      shadow="md"
    >
      <div className="p-3 flex justify-between items-center">
        <div className="flex gap-2">
          <TextBoxEditorTitle label={`${name} opportunites`.toUpperCase()} />
          <div ref={infoRef}>
            <InfoOutlineIcon
              size={16}
              color={ColorHex.natural}
            />
          </div>
          <Tooltip
            placement="top"
            anchorRef={infoRef}
            trigger="mouseenter"
            autoCalcArrowPosition
            arrowDown
          >
            <div className="w-36 p-2.5">
              <TextCaption
                label={`Associate opportunities from ${name} for better tracking`}
                color="text-ds-natural-600"
              />
            </div>
          </Tooltip>
        </div>
        <div className="flex gap-2 items-center">
          <TextCaption label="Owned by:" />
          <DropdownFilter
            capsuleWidth={90}
            withoutPortal
            selected={opportunityFilter}
            items={[
              {
                label: 'Me',
                id: 'opportunitiesOwnedByCurrentUser' as OpportunityFilter,
              },
              {
                label: 'Team',
                id: 'opportunitiesExceptCurrentUser' as OpportunityFilter,
              },
            ]}
            onSelect={(id) => {
              setOpportunityFilter(id);
            }}
            placeholder=""
          />
        </div>
      </div>

      <div className="px-3 pb-3 grow flex flex-col overflow-y-hidden">
        <div
          ref={parentRef}
          className="overflow-y-auto max-h-[130px]"
        >
          <OpportunityList
            ref={parentRef}
            query={debouncedQuery}
            selectedFilter={opportunityFilter}
            setFilter={setOpportunityFilter}
            isIntegrationConnected={isIntegrationConnected}
            isLoading={isLoading}
            opportunities={filteredOpportunities}
          />
        </div>
      </div>

      <Separator horizontal />

      <FreeformTextOption
        value={freeformText}
        disabled={isLoading}
      />
    </Paper>
  );
};
